import React from 'react';

import {
  RecordContextProvider,
  useRecordContext,
} from 'react-admin';

import { get, isEmpty } from 'lodash';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Card,
  Divider,
  Grid,
  Typography,
} from '@mui/material';

import ChevronRight from '@mui/icons-material/ChevronRight';

import Answer from '../Answer';

import FirstName from './FirstName';
import LastName from './LastName';
import TaxResidency from './TaxResidency';
import Pep from './Pep';
import Role from './Role';
import Citizenship from './Citizenship';

import languages, { Languages } from '../../../../constants/languages';
import DateOfBirth from './DateOfBirth';
import NationalIdentifier from './NationalIdentifier';
import Address from './Address';
import City from './City';
import PostalCode from './PostalCode';
import Residence from './Residence';
import conflictFields from '../../../../constants/conflictFields';
import { AssignedOwnersParameters } from '../../questionParameters';
import questions from '../../../../constants/questions';

const LABEL = {
  [languages.EN]: 'Alternative BeneficialOwner',
  [languages.SV]: 'Alternativ verklig huvudman',
  [languages.DE]: 'Alternative/r wirtschaftlich Berechtigte/r',
};

const ValueField = ({ language, extraParameters }: {
  language?: Languages;
  extraParameters?: AssignedOwnersParameters;
}) => {
  const record = useRecordContext();

  const value = get(record, 'value') as {firstName: string; lastName: string}[] | undefined;
  const sx = get(record, 'sx', {});

  if (isEmpty(value)) return null;

  return (
    <Card
      variant="outlined"
      square
      sx={{
        m: 0,
        py: 0,
        width: '100%',
      }}
    >
      <Grid container bgcolor="background.paper">
        {value?.map((altOwner, index) => (
          <Grid
            key={`altOwner-${index + 1}`}
            size={12}
          >
            <Accordion
              key={`${altOwner.firstName} ${altOwner.lastName}`}
              sx={{ maxWidth: null, backgroundColor: 'transparent' }}
            >
              <AccordionSummary
                expandIcon={<ChevronRight />}
                sx={{
                  flexDirection: 'row-reverse',
                  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
                    transform: 'rotate(90deg)',
                  },
                }}
              >
                <Typography
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: '1',
                    WebkitBoxOrient: 'vertical',
                    ml: 4,
                    ...sx,
                  }}
                  variant="h6"
                >
                  {`${altOwner.firstName} ${altOwner.lastName}`}
                </Typography>
              </AccordionSummary>
              <Divider />
              <AccordionDetails>
                <RecordContextProvider value={{ language, ...altOwner, sx }}>
                  <Grid container spacing={6} py="1%" px="1%">
                    <FirstName.Nested extraParameters={extraParameters?.[questions.Q_FIRST_NAME]} />
                    <LastName.Nested extraParameters={extraParameters?.[questions.Q_LAST_NAME]} />
                    <DateOfBirth.Nested
                      extraParameters={extraParameters?.[questions.Q_DATE_OF_BIRTH]}
                    />
                    <Address.Nested extraParameters={extraParameters?.[questions.Q_ADDRESS]} />
                    <PostalCode.Nested
                      extraParameters={extraParameters?.[questions.Q_POSTAL_CODE]}
                    />
                    <City.Nested extraParameters={extraParameters?.[questions.Q_CITY]} />
                    <Residence.Nested extraParameters={extraParameters?.[questions.Q_RESIDENCE]} />
                    <NationalIdentifier.Nested
                      extraParameters={extraParameters?.[questions.Q_NATIONAL_IDENTIFIER]}
                    />
                    <Role.Nested extraParameters={extraParameters?.[questions.Q_ROLE]} />
                    <TaxResidency.Nested
                      extraParameters={extraParameters?.[questions.Q_TAX_RESIDENCY]}
                    />
                    <Citizenship.Nested
                      extraParameters={extraParameters?.[questions.Q_CITIZENSHIP]}
                    />
                    <Pep.Nested extraParameters={extraParameters?.[questions.Q_PEP]} />
                  </Grid>
                </RecordContextProvider>
              </AccordionDetails>
            </Accordion>
            {index + 1 !== value.length && <Divider />}
          </Grid>
        ))}
      </Grid>
    </Card>
  );
};

const AssignedOwners = ({ extraParameters }: {
  extraParameters?: AssignedOwnersParameters;
}) => {
  const record = useRecordContext();

  const language = record?.language as Languages;

  return (
    <Answer
      label={extraParameters?.label ?? LABEL[language]}
      isComplexType
      datapoint={conflictFields.ALTERNATIVE_BENEFICIAL_OWNERS}
      identificationInfo={undefined}
      source="alternativeBeneficialOwners"
    >
      <ValueField language={language} extraParameters={extraParameters} />
    </Answer>
  );
};

AssignedOwners.ValueField = ValueField;

export default AssignedOwners;
